import type { BuyerSubmissionDetailDto } from "@/api/buyers";
import { ResponsiveGrid } from "@/design-system/components/ui";
import { DiligenceChecklistCard } from "./DiligenceChecklistCard";
import { NdaRecordCard } from "./NdaRecordCard";

/**
 * What has to exist on file — the NDA, and the documents the listing broker
 * sends back.
 *
 * <p>They share a tab because they are the same question asked of the same
 * counterparty: what paper does Phoenix hold on this pairing. Nothing here is a
 * status; the NDA in particular is a MILESTONE — a submission does not stop
 * being at Offer/LOI because an NDA is outstanding, and collapsing the two would
 * lose one of the facts.</p>
 *
 * <p>🔴 <b>This tab OWNS the doors.</b> The Overview Rule (Layout Standard
 * §4.1a) says an Overview may summarise, reference and link but may not mutate
 * or send, and resolves the tension with §6.3 by putting the door on the tab
 * that owns the subject. This is that tab: every control that records an NDA
 * fact, adds a note or stamps a diligence date lives here, and Overview's
 * milestone rows are read-only rows pointing at it.</p>
 *
 * <p>Seller prequalification is deliberately NOT here despite being paperwork.
 * It lives immediately above Escalate to a deal, because the escalation gate
 * warns when it is missing and the control that clears the warning has to be the
 * next thing the eye lands on.</p>
 *
 * <p><b>Two cards, side by side where there is room for two.</b>
 * `ResponsiveGrid` reflows on the width of the BOX IT IS IN rather than the
 * viewport, so the same body is two columns on the detail page and one inside a
 * record drawer — where a `lg:grid-cols-2` would have kept a page-width grid in
 * a 420px panel and clamped every value in it.</p>
 */
export function SubmissionPaperworkTab({
  s,
  canWrite,
  onOpenDocuments,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
  /** Optional: the host's jump to the Documents tab, where the paper lives. */
  onOpenDocuments?: () => void;
}) {
  return (
    <ResponsiveGrid cols={2} gap="lg" minColWidth="26rem">
      <div className="min-w-0">
        <NdaRecordCard s={s} canWrite={canWrite} />
      </div>
      <div className="min-w-0">
        <DiligenceChecklistCard
          s={s}
          canWrite={canWrite}
          onOpenDocuments={onOpenDocuments}
        />
      </div>
    </ResponsiveGrid>
  );
}
